import React, { useState, useRef } from 'react';
import { Camera, Upload, X, Eye, RotateCcw, Download, Trash2 } from 'lucide-react';

const PhotoUpload = ({ onPhotoUpload, existingPhotos = [], vehicleId, inspectionType = 'pre-trip' }) => {
  const [photos, setPhotos] = useState(existingPhotos);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showCategorySelector, setShowCategorySelector] = useState(false);
  const [pendingFiles, setPendingFiles] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('other');
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  // Define photo categories based on inspection type
  const getPhotoCategories = () => {
    if (inspectionType === 'load-inspection') {
      return [
        { id: 'front_plate', label: 'Front of Vehicle (Plate Visible)', required: true },
        { id: 'back', label: 'Back of Vehicle', required: true },
        { id: 'left_side', label: 'Left Side', required: true },
        { id: 'right_side', label: 'Right Side', required: true },
        { id: 'trailer', label: 'Trailer', required: true }
      ];
    }

    // Default categories for pre-trip and post-trip inspections
    return [
      { id: 'exterior_front', label: 'Front of Vehicle', required: true },
      { id: 'exterior_back', label: 'Back of Vehicle', required: true },
      { id: 'exterior_left', label: 'Left Side', required: false },
      { id: 'exterior_right', label: 'Right Side', required: false },
      { id: 'dashboard', label: 'Dashboard/Gauges', required: true },
      { id: 'interior', label: 'Interior Condition', required: false },
      { id: 'engine_bay', label: 'Engine Bay', required: false },
      { id: 'defects', label: 'Defects/Issues', required: false },
      { id: 'other', label: 'Other', required: false }
    ];
  };

  const photoCategories = getPhotoCategories();

  const handleFileSelect = async (files, category = 'other') => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const newPhotos = [];

    for (let file of Array.from(files)) {
      if (file.type.startsWith('image/')) {
        const photoData = {
          id: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${performance.now()}`.replace(/\./g, ''),
          file: file,
          preview: URL.createObjectURL(file),
          category: category,
          timestamp: new Date().toISOString(),
          vehicleId: vehicleId,
          size: file.size,
          name: file.name,
          notes: ''
        };
        newPhotos.push(photoData);
      }
    }

    const updatedPhotos = [...photos, ...newPhotos];
    setPhotos(updatedPhotos);
    onPhotoUpload(updatedPhotos);
    setUploading(false);
  };

  const handleCameraCapture = (e, category = null) => {
    if (e.target.files && e.target.files.length > 0) {
      if (category) {
        handleFileSelect(e.target.files, category);
      } else {
        setPendingFiles(e.target.files);
        setShowCategorySelector(true);
      }
    }
  };

  const handleFileUpload = (e, category = null) => {
    if (e.target.files && e.target.files.length > 0) {
      if (category) {
        handleFileSelect(e.target.files, category);
      } else {
        setPendingFiles(e.target.files);
        setShowCategorySelector(true);
      }
    }
  };

  const handleCategoryConfirm = () => {
    if (pendingFiles) {
      handleFileSelect(pendingFiles, selectedCategory);
      setPendingFiles(null);
      setShowCategorySelector(false);
      setSelectedCategory('other');
    }
  };

  const handleCategoryCancel = () => {
    setPendingFiles(null);
    setShowCategorySelector(false);
    setSelectedCategory('other');
    // Reset file inputs
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const removePhoto = (photoId) => {
    const updatedPhotos = photos.filter(photo => photo.id !== photoId);
    setPhotos(updatedPhotos);
    onPhotoUpload(updatedPhotos);
  };

  const updatePhotoNotes = (photoId, notes) => {
    const updatedPhotos = photos.map(photo =>
      photo.id === photoId ? { ...photo, notes } : photo
    );
    setPhotos(updatedPhotos);
    onPhotoUpload(updatedPhotos);
  };

  const updatePhotoCategory = (photoId, category) => {
    const updatedPhotos = photos.map(photo =>
      photo.id === photoId ? { ...photo, category } : photo
    );
    setPhotos(updatedPhotos);
    onPhotoUpload(updatedPhotos);
  };

  const getPhotosByCategory = (categoryId) => {
    return photos.filter(photo => photo.category === categoryId);
  };

  const getRequiredCategoriesStatus = () => {
    const required = photoCategories.filter(cat => cat.required);
    const completed = required.filter(cat => getPhotosByCategory(cat.id).length > 0);
    return { completed: completed.length, total: required.length };
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const CategorySelector = ({ onConfirm, onCancel, fileCount }) => {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
        <div className="bg-white rounded-lg max-w-md w-full mx-4">
          <div className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Select Category for Photo{fileCount > 1 ? 's' : ''}
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Choose the category for your {fileCount} photo{fileCount > 1 ? 's' : ''} to help organize your inspection.
            </p>
            
            <div className="space-y-3">
              {photoCategories.map(category => (
                <label key={category.id} className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="category"
                    value={category.id}
                    checked={selectedCategory === category.id}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-gray-900">
                        {category.label}
                      </span>
                      {category.required && (
                        <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-full">
                          Required
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
            
            <div className="flex justify-end space-x-3 mt-8">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
              >
                Add Photo{fileCount > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const PhotoModal = ({ photo, onClose }) => {
    const [notes, setNotes] = useState(photo.notes || '');
    const [category, setCategory] = useState(photo.category);

    const handleSave = () => {
      updatePhotoNotes(photo.id, notes);
      updatePhotoCategory(photo.id, category);
      onClose();
    };

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-2 sm:p-4">
        <div className="bg-white rounded-lg max-w-4xl max-h-[95vh] w-full overflow-hidden">
          <div className="flex items-center justify-between p-3 sm:p-4 border-b">
            <h3 className="text-base sm:text-lg font-semibold">Photo Details</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>
          
          <div className="p-3 sm:p-4 overflow-auto max-h-[calc(95vh-120px)]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <img
                  src={photo.preview}
                  alt="Inspection"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
                <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600 space-y-1">
                  <p><strong>File:</strong> {photo.name}</p>
                  <p><strong>Size:</strong> {formatFileSize(photo.size)}</p>
                  <p><strong>Taken:</strong> {new Date(photo.timestamp).toLocaleString()}</p>
                </div>
              </div>
              
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Category
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full p-2 sm:p-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {photoCategories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.label} {cat.required ? '(Required)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add notes about this photo..."
                    rows={4}
                    className="w-full p-2 sm:p-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-2 p-3 sm:p-4 border-t">
            <button
              onClick={onClose}
              className="px-3 sm:px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-3 sm:px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
            >
              Save Changes
            </button>
          </div>
        </div>
      </div>
    );
  };

  const requiredStatus = getRequiredCategoriesStatus();

  return (
    <div className="space-y-6">
      {/* Header with Progress */}
      <div className="bg-white rounded-lg shadow p-4 md:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Inspection Photos</h2>
          <div className="flex flex-col sm:text-right space-y-1">
            <div className="text-base sm:text-lg font-medium text-gray-900">
              {photos.length} photo{photos.length !== 1 ? 's' : ''}
            </div>
            <div className="text-xs sm:text-sm text-gray-600">
              {requiredStatus.completed}/{requiredStatus.total} required categories
            </div>
          </div>
        </div>


        {uploading && (
          <div className="mt-4 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-sm text-gray-600 mt-2">Uploading photos...</p>
          </div>
        )}
      </div>

      {/* Photo Categories */}
      {photoCategories.map(category => {
        const categoryPhotos = getPhotosByCategory(category.id);
        
        return (
          <div key={category.id} className="bg-white rounded-lg shadow">
            <div className="p-3 sm:p-4 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                <div className="flex items-center space-x-2">
                  <h3 className="text-sm sm:text-base font-medium text-gray-900">{category.label}</h3>
                  {category.required && (
                    <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-full">
                      Required
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <span className="text-xs sm:text-sm text-gray-600">
                    {categoryPhotos.length} photo{categoryPhotos.length !== 1 ? 's' : ''}
                  </span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.capture = 'environment';
                        input.multiple = true;
                        input.onchange = (e) => handleCameraCapture(e, category.id);
                        input.click();
                      }}
                      className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors"
                      disabled={uploading}
                      title="Take Photo"
                    >
                      <Camera className="h-4 w-4 mr-2" />
                      Take
                    </button>
                    <button
                      onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file';
                        input.accept = 'image/*';
                        input.multiple = true;
                        input.onchange = (e) => handleFileUpload(e, category.id);
                        input.click();
                      }}
                      className="flex items-center px-3 py-2 bg-gray-600 text-white rounded-md text-sm hover:bg-gray-700 transition-colors"
                      disabled={uploading}
                      title="Upload Files"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Upload
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-3 sm:p-4">
              {categoryPhotos.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                  {categoryPhotos.map(photo => (
                    <div key={photo.id} className="relative group">
                      <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                        <img
                          src={photo.preview}
                          alt="Inspection"
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {/* Mobile: Always visible controls at bottom */}
                      <div className="absolute bottom-2 left-2 right-2 flex justify-center space-x-2 sm:hidden">
                        <button
                          onClick={() => setSelectedPhoto(photo)}
                          className="p-2 bg-white text-gray-900 rounded-full shadow-lg hover:bg-gray-100"
                          title="View Details"
                        >
                          <Eye className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => removePhoto(photo.id)}
                          className="p-2 bg-red-600 text-white rounded-full shadow-lg hover:bg-red-700"
                          title="Delete Photo"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                      
                      {/* Desktop: Hover overlay */}
                      <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg sm:flex items-center justify-center space-x-2 hidden">
                        <button
                          onClick={() => setSelectedPhoto(photo)}
                          className="p-2 bg-white text-gray-900 rounded-full hover:bg-gray-100"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => removePhoto(photo.id)}
                          className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700"
                          title="Delete Photo"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      
                      {photo.notes && (
                        <div className="absolute top-2 right-2 w-3 h-3 bg-blue-600 rounded-full shadow-lg" title="Has notes" />
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 sm:py-8 text-gray-500">
                  <Camera className="h-8 w-8 sm:h-12 sm:w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm sm:text-base">No photos in this category</p>
                  {category.required && (
                    <p className="text-xs sm:text-sm text-red-600 mt-1">This category is required</p>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Category Selector Modal */}
      {showCategorySelector && pendingFiles && (
        <CategorySelector
          onConfirm={handleCategoryConfirm}
          onCancel={handleCategoryCancel}
          fileCount={pendingFiles.length}
        />
      )}

      {/* Photo Modal */}
      {selectedPhoto && (
        <PhotoModal
          photo={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
        />
      )}
    </div>
  );
};

export default PhotoUpload;